import { Shield, Cloud, RefreshCw, Mail, Database, Lock, ArrowRight, Sparkles, CheckCircle2, Code2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";

const SecurityServices = () => {
  const acronisServices = [
    {
      icon: Shield,
      title: "Advanced Security",
      subtitle: "Antimalware e Antirransomware",
      description: "Conte com uma defesa abrangente contra as ameaças cibernéticas atuais.",
      color: "from-blue-500/20 to-blue-600/20",
      features: [
        "Proteção em tempo real",
        "Inteligência Artificial multilayered",
        "Detecção de ameaças avançadas",
        "Proteção contra ransomware"
      ]
    },
    {
      icon: Cloud,
      title: "Cyber Backup Cloud",
      subtitle: "Proteção abrangente",
      description: "Aqui você terá a solução mais avançada de mercado, quando o assunto é proteção e backup.",
      color: "from-purple-500/20 to-purple-600/20",
      features: [
        "Backup em nuvem seguro",
        "Recuperação rápida de dados",
        "Criptografia de ponta a ponta",
        "Armazenamento ilimitado"
      ]
    },
    {
      icon: RefreshCw,
      title: "Advanced Disaster Recovery",
      subtitle: "Recuperação de desastre",
      description: "Pronto para restaurar seus dados e sistemas em segundos, sem perda de informações.",
      color: "from-green-500/20 to-green-600/20",
      features: [
        "Recuperação instantânea",
        "Solução pronta de DRaaS",
        "Ambientes virtuais e físicos",
        "Baseado no Acronis Cyber Backup Cloud"
      ]
    },
    {
      icon: Database,
      title: "Universal Restore",
      subtitle: "Restauração universal",
      description: "Restaura o sistema operacional em minutos sem depender da máquina, independentemente de plataforma utilizada.",
      color: "from-emerald-500/20 to-emerald-600/20",
      features: [
        "Restauração multiplataforma",
        "Recuperação rápida",
        "Independente de hardware",
        "Compatível com qualquer sistema"
      ]
    },
    {
      icon: Lock,
      title: "Cyber Notary Cloud",
      subtitle: "Assinatura digital certificada",
      description: "Excelente para reconhecimento de firma, assinatura eletrônica e verificação de arquivos em empresas de qualquer tamanho.",
      color: "from-indigo-500/20 to-indigo-600/20",
      features: [
        "Assinatura eletrônica certificada",
        "Verificação de autenticidade",
        "Blockchain technology",
        "Conformidade legal"
      ]
    },
    {
      icon: Mail,
      title: "Advanced Email Security",
      subtitle: "Segurança de e-mail",
      description: "Intercepte imediatamente qualquer ataque vindo por e-mail.",
      color: "from-red-500/20 to-red-600/20",
      features: [
        "Bloqueio de spam e phishing",
        "Proteção contra malware",
        "Análise de comportamento",
        "Quarentena inteligente"
      ]
    }
  ];

  const additionalServices = [
    {
      title: "Advanced Backup",
      description: "Solução híbrida de backup em nuvem como serviço para provedores de serviços",
      icon: Cloud
    },
    {
      title: "Advanced Management",
      description: "Simplifique a gestão de proteção do cliente e automação de patches integradas em uma única solução",
      icon: Database
    },
    {
      title: "Advanced Security + EDR",
      description: "Simplifique a segurança de endpoint - Solução completa de proteção cibernética desenvolvida para MSPs",
      icon: Shield
    },
    {
      title: "Advanced Data Loss Prevention",
      description: "Controles sensíveis ao contexto e ao conteúdo - Incremente sua tecnologia de serviços de segurança com uma prevenção de perda de dados simplificada",
      icon: Lock
    }
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Vertical lines pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px)] bg-[size:8rem_100%] pointer-events-none" style={{ maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', WebkitMaskComposite: 'source-in' }} />

      {/* Header */}
      <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
          </Link>
          <nav className="hidden md:flex gap-6">
            <Link to="/#servicos" className="text-muted-foreground hover:text-primary transition-colors">
              Serviços
            </Link>
            <Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">
              Blog
            </Link>
            <Link to="/seguranca-digital" className="text-primary font-medium transition-colors">
              Segurança Digital
            </Link>
            <Link to="/contato" className="text-muted-foreground hover:text-primary transition-colors">
              Contato
            </Link>
          </nav>
          <Button asChild variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary">
            <a href="https://folio.app.br/area-cliente">Área do Cliente</a>
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-32 px-4 relative">
        <div className="container mx-auto text-center relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8">
            <ArrowRight className="h-4 w-4 rotate-180" />
            Voltar para Home
          </Link>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6 backdrop-blur-sm">
            <Sparkles className="h-4 w-4 text-primary animate-pulse" />
            <span className="text-sm font-medium text-primary">Parceiro Oficial Acronis</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 max-w-5xl mx-auto leading-tight">
            Conheça o{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
              TOTAL SECURITY
            </span>
          </h1>

          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Facilidade, estabilidade e proteção digital no dia a dia de sua empresa
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="gap-2 group">
              <Link to="/contato">
                <span className="flex items-center gap-2">
                  Solicitar Demonstração <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/contato">Falar com Especialista</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
              Plataforma Completa de Segurança Digital
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Por meio de uma única plataforma integrada, você fornece aos seus clientes backup em nuvem, antivírus,
              antiransomware, antimalware, disaster recovery e muito mais!
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {acronisServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={index}
                  className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(99,159,255,0.2)] hover:scale-[1.02] group"
                >
                  <CardHeader>
                    <div className={`h-16 w-16 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform relative`}>
                      <Icon className="h-8 w-8 text-primary relative z-10" />
                      <div className="absolute inset-0 bg-primary/30 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <CardTitle className="text-xl text-foreground">{service.title}</CardTitle>
                    <CardDescription className="text-sm font-semibold text-primary">
                      {service.subtitle}
                    </CardDescription>
                    <CardDescription className="text-base text-muted-foreground mt-2">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 px-4 relative bg-card/30">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Soluções Adicionais Acronis
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Expanda ainda mais a proteção da sua empresa com nossas soluções complementares
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={index}
                  className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group"
                >
                  <CardHeader>
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg text-foreground">{service.title}</CardTitle>
                    <CardDescription className="text-sm text-muted-foreground">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto relative z-10">
          <Card className="bg-gradient-to-br from-primary/20 via-card/50 to-accent/20 border-primary/30 backdrop-blur-xl overflow-hidden relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/10 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
            <CardHeader className="text-center pb-6 relative z-10">
              <CardTitle className="text-3xl md:text-5xl mb-4 text-foreground">
                Proteja sua empresa agora
              </CardTitle>
              <CardDescription className="text-foreground/80 text-lg max-w-2xl mx-auto">
                Entre em contato e descubra como as soluções Acronis podem transformar a segurança digital da sua empresa
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center relative z-10">
              <Button size="lg" asChild className="gap-2 group/btn">
                <Link to="/contato">
                  <span className="flex items-center gap-2">
                    Solicitar Orçamento <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default SecurityServices;
